import { YYYY_MM_DD } from "constants/date";
import {
  NOT_SPECIFIED,
  OTHER_ITEM,
  PERIOD,
  PeriodType,
} from "constants/revenueMetrics";
import { TOTALS_COLUMN } from "constants/reports";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import { useElementDimensions } from "hooks/useElementDimensions";
import { ReportsType } from "pages/Books/Reports/Reports";
import { ColumnDef, Row } from "react-table-8.10.7";
import { Account, ComparisonReportData } from "types/Models/report";
import { formatDateByViewType } from "utils/formatDate";
import { ReportFilterType } from "./ReportFilter";
import { TransactionsTable } from "./ReportsAccordion";
import { ReactNode } from "react";

export type ExpandedCellType = {
  rowId: string;
  colId: string;
} | null;

export const getTransactionReportHeading = (
  columnName: string,
  name: string,
  viewBy?: string | null
) => {
  const formattedDate = dayjs(columnName).isValid()
    ? formatDateByViewType(columnName, (viewBy as PeriodType) || PERIOD.MONTHLY)
    : columnName;

  return `${formattedDate} : ${name}`;
};

type ExpandedTransactionProps = {
  setExpandedCell: React.Dispatch<React.SetStateAction<ExpandedCellType>>;
  expandedCell: ExpandedCellType;
  reportData: ComparisonReportData;
  filter: ReportFilterType;
  row: Row<Account>;
  columns: ColumnDef<Account, any>[];
  columnWidths: Record<string, number>;
  scrollContainerWidth: number;
  reportType: ReportsType;
  selectedRows: {};
  setSelectedRows: React.Dispatch<React.SetStateAction<{}>>;
};

export const ExpandedRow = ({
  colSpan,
  leftPinWidth,
  rightPinWidth,
  scrollContainerWidth,
  children,
}: {
  colSpan: number;
  leftPinWidth: number;
  rightPinWidth: number;
  scrollContainerWidth: number;
  children: ReactNode;
}) => {
  const [ref, { height }] = useElementDimensions();

  return (
    <motion.tr
      initial={{ height: 0, opacity: 0 }}
      animate={{ height, opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.05 }}
      className="t-w-full t-z-filter-head t-overscroll-x-none"
    >
      <td
        colSpan={colSpan}
        className="t-p-0 t-sticky t-left-0 t-overflow-hidden"
        style={{
          height,
          maxHeight: height,
          width: scrollContainerWidth - rightPinWidth,
          maxWidth: scrollContainerWidth - rightPinWidth,
        }}
      >
        <div className="t-flex t-h-full">
          <div
            className="t-flex-shrink-0 t-drop-shadow-table-left-pin t-bg-white t-z-[3]"
            style={{ width: `${leftPinWidth}px` }}
          />
          <div
            className="t-px-4 t-py-4 t-flex-1 t-min-w-0 t-overflow-hidden t-border-0 t-border-l-2 t-border-solid t-border-neutral-20"
            ref={ref}
          >
            {children}
          </div>
        </div>
      </td>
    </motion.tr>
  );
};

export const ExpandedTransaction = ({
  setExpandedCell,
  expandedCell,
  reportData,
  filter,
  row,
  columns,
  columnWidths,
  scrollContainerWidth,
  reportType,
  selectedRows,
  setSelectedRows,
}: ExpandedTransactionProps) => {
  const [ref, { height: expandedRowHeight }] = useElementDimensions();
  const nameColId = columns[0]?.id as string | undefined;
  const actionsColId = columns[columns.length - 1]?.id as string | undefined;
  const leftPinWidth =
    (nameColId && columnWidths[nameColId]) || columns[0]?.size || 300;
  const rightPinWidth =
    (actionsColId && columnWidths[actionsColId]) ||
    columns[columns.length - 1]?.size ||
    90;
  const isGroupedReport = Boolean(filter.TXN_GROUP_ID) || Boolean(filter.OTHER);

  const getStartDate = (date: string) => {
    return expandedCell ? dayjs(date).startOf("month").format(YYYY_MM_DD) : "";
  };

  const getEndDate = (date: string) => {
    return expandedCell ? date : "";
  };

  const getTransactionsReportHeading = () => {
    if (isGroupedReport) return `${expandedCell?.colId} : ${row.original.name}`;

    if (expandedCell?.colId === TOTALS_COLUMN) {
      return `All Transactions: ${row.original.name}`;
    }

    return getTransactionReportHeading(
      getEndDate(reportData[expandedCell?.colId || ""]?.end_date || ""),
      row.original.name,
      filter.VIEW_BY
    );
  };

  const tableProps = {
    startDate: isGroupedReport
      ? filter.START_DATE
      : getStartDate(reportData[expandedCell?.colId || ""]?.start_date || ""),

    endDate: isGroupedReport
      ? filter.END_DATE
      : getEndDate(reportData[expandedCell?.colId || ""]?.end_date || ""),

    transactionsReportHeading: getTransactionsReportHeading(),

    classId: Boolean(filter.TXN_GROUP_ID)
      ? reportData[expandedCell?.colId || ""]?.class_id || NOT_SPECIFIED
      : "",

    merchantId: Boolean(filter.OTHER === OTHER_ITEM.MERCHANT)
      ? reportData[expandedCell?.colId || ""]?.merchant_id || NOT_SPECIFIED
      : "",
  };

  return (
    <motion.tr
      key={`${row.id}-expanded-${expandedCell?.colId}`}
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: expandedRowHeight, opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.05 }}
      className="t-w-full t-z-filter-head"
    >
      <td
        colSpan={row.getVisibleCells().length - 1}
        className="t-p-0"
        style={{
          height: expandedRowHeight,
          maxHeight: expandedRowHeight,
        }}
      >
        <div
          className="t-flex t-h-full t-sticky t-left-0 t-overflow-hidden"
          style={{
            width: scrollContainerWidth - rightPinWidth,
            maxWidth: scrollContainerWidth - rightPinWidth,
          }}
        >
          <div
            className="t-flex-shrink-0 t-drop-shadow-table-left-pin t-bg-white t-z-[3]"
            style={{ width: `${leftPinWidth}px` }}
          />
          <div
            className="t-px-4 t-py-4 t-flex-1 t-min-w-0 t-overflow-hidden t-border-0 t-border-l-2 t-border-solid t-border-neutral-20"
            ref={ref}
          >
            <TransactionsTable
              reportType={reportType}
              categoryId={row.original.uuid}
              classId={tableProps.classId}
              merchantId={tableProps.merchantId}
              startDate={tableProps.startDate}
              endDate={tableProps.endDate}
              transactionGroupId={filter.TXN_GROUP_ID}
              accountingMethod={filter.ACCOUNTING_METHOD}
              isComparisonReport
              transactionsReportHeading={tableProps.transactionsReportHeading}
              handleClose={() => setExpandedCell(null)}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              fetch_ungrouped_txn_ids={filter.NO_CLASSIFICATION}
              transaction_group_ids={filter.CLASSIFICATION}
              class_ids={filter.CLASS_IDS}
            />
          </div>
        </div>
      </td>
      <td
        className="t-p-0 t-sticky t-right-0 t-align-top t-drop-shadow-table-right-pin t-bg-white t-z-[3]"
        style={{
          width: `${rightPinWidth}px`,
          minWidth: `${rightPinWidth}px`,
          height: expandedRowHeight,
        }}
      />
    </motion.tr>
  );
};
